import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { schoolDb } from './schoolFirebase';
import type {
  PasukProgress,
  PasukRef,
  Recording,
  School,
  SchoolClass,
  Student,
  StudentProgress,
  Teacher,
} from '../types';

type Data = Record<string, unknown>;

const str = (v: unknown, fallback = ''): string => (typeof v === 'string' ? v : fallback);
const num = (v: unknown, fallback = 0): number => (typeof v === 'number' ? v : fallback);
const ts = (v: unknown): Timestamp | null => (v instanceof Timestamp ? v : null);

function toPasukRef(v: unknown): PasukRef | null {
  if (!v || typeof v !== 'object') return null;
  const o = v as Data;
  if (typeof o.book !== 'string' || typeof o.chapter !== 'number' || typeof o.pasuk !== 'number') {
    return null;
  }
  return { book: o.book, chapter: o.chapter, pasuk: o.pasuk };
}

function toSchoolClass(id: string, d: Data): SchoolClass {
  return {
    id,
    schoolId: str(d.schoolId),
    name: str(d.name),
    joinCode: str(d.joinCode),
    teacherEmails: Array.isArray(d.teacherEmails) ? d.teacherEmails.map((e) => str(e)) : [],
    currentPasuk: toPasukRef(d.currentPasuk),
    nusach: typeof d.nusach === 'string' ? d.nusach : undefined,
    rabbiKey: typeof d.rabbiKey === 'string' ? d.rabbiKey : undefined,
  };
}

function toStudent(id: string, d: Data): Student {
  return {
    id,
    schoolId: str(d.schoolId),
    classId: str(d.classId),
    firstName: str(d.firstName),
    lastName: str(d.lastName),
    loginCode: str(d.loginCode),
    active: d.active !== false,
  };
}

function toProgress(id: string, d: Data): StudentProgress {
  const raw = d.pesukim && typeof d.pesukim === 'object' ? (d.pesukim as Record<string, Data>) : {};
  const pesukim: Record<string, PasukProgress> = {};
  Object.entries(raw).forEach(([key, p]) => {
    pesukim[key] = {
      attempts: num(p?.attempts),
      bestScore: num(p?.bestScore),
      lastScore: num(p?.lastScore),
      lastAt: ts(p?.lastAt),
    };
  });
  return {
    studentId: id,
    schoolId: str(d.schoolId),
    classId: str(d.classId),
    pesukim,
    lastActivityAt: ts(d.lastActivityAt),
  };
}

function toRecording(id: string, d: Data): Recording {
  return {
    id,
    schoolId: str(d.schoolId),
    classId: str(d.classId),
    studentId: str(d.studentId),
    pasukKey: str(d.pasukKey),
    pasuk: toPasukRef(d.pasuk) ?? { book: '', chapter: 0, pasuk: 0 },
    audioUrl: str(d.audioUrl),
    score: num(d.score),
    explanation: str(d.explanation),
    createdAt: ts(d.createdAt),
  };
}

// ─── Reads ──────────────────────────────────────────────────────────────────

export async function getTeacher(email: string): Promise<Teacher | null> {
  const snap = await getDoc(doc(schoolDb, 'schoolTeachers', email));
  if (!snap.exists()) return null;
  const d = snap.data();
  return {
    email,
    schoolId: str(d.schoolId),
    name: str(d.name),
    classIds: Array.isArray(d.classIds) ? d.classIds.map((c: unknown) => str(c)) : [],
  };
}

/** schoolSessions/{uid}, written by the schoolStudentLogin function. */
export async function getStudentSession(
  uid: string,
): Promise<{ schoolId: string; classId: string; studentId: string } | null> {
  const snap = await getDoc(doc(schoolDb, 'schoolSessions', uid));
  if (!snap.exists()) return null;
  const d = snap.data();
  if (d.role !== 'student') return null;
  const schoolId = str(d.schoolId);
  const classId = str(d.classId);
  const studentId = str(d.studentId);
  return schoolId && classId && studentId ? { schoolId, classId, studentId } : null;
}

export async function getSchool(schoolId: string): Promise<School | null> {
  const snap = await getDoc(doc(schoolDb, 'schools', schoolId));
  if (!snap.exists()) return null;
  const d = snap.data();
  return { id: snap.id, name: str(d.name), city: typeof d.city === 'string' ? d.city : undefined };
}

export async function getClass(classId: string): Promise<SchoolClass | null> {
  const snap = await getDoc(doc(schoolDb, 'schoolClasses', classId));
  return snap.exists() ? toSchoolClass(snap.id, snap.data()) : null;
}

export async function getClassesForTeacher(teacher: Teacher): Promise<SchoolClass[]> {
  // Read by id (teacher.classIds) rather than querying, so no composite index is needed.
  const classes = await Promise.all(teacher.classIds.map((id) => getClass(id)));
  return classes
    .filter((c): c is SchoolClass => c !== null && c.schoolId === teacher.schoolId)
    .sort((a, b) => a.name.localeCompare(b.name, 'he'));
}

export async function getStudent(studentId: string): Promise<Student | null> {
  const snap = await getDoc(doc(schoolDb, 'schoolStudents', studentId));
  return snap.exists() ? toStudent(snap.id, snap.data()) : null;
}

export async function getStudentsInClass(schoolId: string, classId: string): Promise<Student[]> {
  const snap = await getDocs(
    query(
      collection(schoolDb, 'schoolStudents'),
      where('schoolId', '==', schoolId),
      where('classId', '==', classId),
    ),
  );
  return snap.docs
    .map((d) => toStudent(d.id, d.data()))
    .filter((s) => s.active)
    .sort((a, b) =>
      `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`, 'he'),
    );
}

export async function getStudentProgress(studentId: string): Promise<StudentProgress | null> {
  const snap = await getDoc(doc(schoolDb, 'schoolStudentProgress', studentId));
  return snap.exists() ? toProgress(snap.id, snap.data()) : null;
}

export async function getProgressForClass(
  schoolId: string,
  classId: string,
): Promise<Record<string, StudentProgress>> {
  const snap = await getDocs(
    query(
      collection(schoolDb, 'schoolStudentProgress'),
      where('schoolId', '==', schoolId),
      where('classId', '==', classId),
    ),
  );
  const result: Record<string, StudentProgress> = {};
  snap.docs.forEach((d) => {
    result[d.id] = toProgress(d.id, d.data());
  });
  return result;
}

export async function getRecordingsForStudent(
  schoolId: string,
  studentId: string,
  max = 50,
): Promise<Recording[]> {
  const snap = await getDocs(
    query(
      collection(schoolDb, 'schoolRecordings'),
      where('schoolId', '==', schoolId),
      where('studentId', '==', studentId),
      orderBy('createdAt', 'desc'),
      limit(max),
    ),
  );
  return snap.docs.map((d) => toRecording(d.id, d.data()));
}

// ─── Teacher writes ─────────────────────────────────────────────────────────

export async function setClassCurrentPasuk(classId: string, pasuk: PasukRef): Promise<void> {
  await updateDoc(doc(schoolDb, 'schoolClasses', classId), {
    currentPasuk: pasuk,
    updatedAt: serverTimestamp(),
  });
}

function randomLoginCode(): string {
  return String(Math.floor(1000 + Math.random() * 9000));
}

/** Creates a student with a 4-digit login code that is unique within the class. */
export async function addStudent(
  schoolClass: SchoolClass,
  firstName: string,
  lastName: string,
  existing: Student[],
): Promise<Student> {
  const taken = new Set(existing.map((s) => s.loginCode));
  let loginCode = randomLoginCode();
  for (let i = 0; i < 50 && taken.has(loginCode); i += 1) {
    loginCode = randomLoginCode();
  }
  if (taken.has(loginCode)) {
    throw new Error('Could not generate a unique login code');
  }
  const data = {
    schoolId: schoolClass.schoolId,
    classId: schoolClass.id,
    firstName: firstName.trim(),
    lastName: lastName.trim(),
    loginCode,
    active: true,
    createdAt: serverTimestamp(),
  };
  const ref = await addDoc(collection(schoolDb, 'schoolStudents'), data);
  return { id: ref.id, ...data, active: true };
}
